import { clampFreeText } from './clamp-text.js';

/**
 * Kommentar der Kasse und „Problem melden“ (U17f, Plan 16 Abschnitt 4.10.7). Der Weg ist der Sync der Kasse (Gerätesitzung,
 * Outbox, Idempotenz), nicht die Nachrichten: Im Schnellzugang (Gerät ohne Konto) gingen Nachrichten gar nicht.
 *
 * Datenschutz: Der Freitext ist auf `REGISTER_NOTE_MAX` Zeichen begrenzt, enthält keine Personendaten (Hinweis unter dem Feld),
 * kommt nie in Log, Audit oder Fehlerbericht und ist nur mit `event.reports.view` sichtbar.
 */
export const REGISTER_NOTE_TOPICS = [
  'label_shown',
  'label_damaged',
  'price_manual',
  'item_missing',
  'change_low',
  'device_problem',
  'please_come',
  'other',
] as const;
export type RegisterNoteTopic = (typeof REGISTER_NOTE_TOPICS)[number];

export const REGISTER_NOTE_MAX = 300;
/** Höchstzahl der Themen je Kommentar. */
export const REGISTER_NOTE_MAX_TOPICS = 4;
/** Grenzen am Server: je Aufruf und je Kasse und Tag (darüber `rejected` mit `NOTE_LIMIT`, der Stapel bleibt gültig). */
export const REGISTER_NOTES_PER_SYNC = 20;
export const REGISTER_NOTES_PER_DAY = 200;

export const REGISTER_NOTE_TOPIC_LABELS: Readonly<Record<RegisterNoteTopic, string>> = {
  label_shown: 'Kunde hat Etikett vorgelegt',
  label_damaged: 'Etikett beschädigt oder unlesbar',
  price_manual: 'Preis per Hand korrigiert',
  item_missing: 'Artikel fehlte in der Liste',
  change_low: 'Wechselgeld knapp',
  device_problem: 'Gerät oder Scanner streikt',
  please_come: 'Bitte kommen',
  other: 'Anderes',
};

/** Schnellwahl zu einem Verkauf bzw. ohne Verkauf („Problem melden“ aus dem Menü). */
export const SALE_NOTE_TOPICS = [
  'label_shown',
  'label_damaged',
  'price_manual',
  'item_missing',
  'other',
] as const satisfies readonly RegisterNoteTopic[];
export const FREE_NOTE_TOPICS = [
  'change_low',
  'device_problem',
  'please_come',
  'other',
] as const satisfies readonly RegisterNoteTopic[];

export interface RegisterNotePayload {
  /** Idempotenz: dieselbe Kennung ergibt `duplicate`. */
  clientUuid: string;
  /** Ohne: „Problem melden“ ohne Verkauf. */
  saleClientUuid?: string;
  lineNo?: number;
  topics: RegisterNoteTopic[];
  text?: string;
  /** ISO, Zeit des Geräts. */
  createdAt: string;
}

export type RegisterNoteStatus = 'saved' | 'duplicate' | 'unknown_sale' | 'rejected';

export interface RegisterNoteOutcome {
  clientUuid: string;
  status: RegisterNoteStatus;
  /** Bei `rejected`: `NOTE_LIMIT`. */
  code?: 'NOTE_LIMIT';
}

export function isRegisterNoteTopic(value: unknown): value is RegisterNoteTopic {
  return typeof value === 'string' && (REGISTER_NOTE_TOPICS as readonly string[]).includes(value);
}

/** Kürzt den Freitext auf `REGISTER_NOTE_MAX` Zeichen; leer ergibt `undefined`. */
export function clampRegisterNoteText(text: string | undefined): string | undefined {
  return clampFreeText(text, REGISTER_NOTE_MAX, true);
}

/** Klartext der Themen, getrennt durch „ · “ (Anzeige bei der Leitung). */
export function registerNoteTopicsText(topics: readonly string[]): string {
  return topics
    .filter(isRegisterNoteTopic)
    .map((topic) => REGISTER_NOTE_TOPIC_LABELS[topic])
    .join(' · ');
}
